import * as React from "react";
import { some, trim, uniq } from "lodash";
import { BktDataTable } from "front-end-lib";
import { IBktDataTableData, ISelectData } from "front-end-lib/core";
import {
  defaultDataTableConfig,
  assignmentRulesDataTableHeaders
} from "../../../config";
import { messages } from "../../../constants";
import { MddDataContainer, withMddStudy } from "../../../components";
import { IMddStudyProps, IAssignmentRule, IFilter } from "../../../model";
import { eAssignmentRuleSaga } from "../../../sagas";
import { useSaga, filterDataForDataTable, history } from "../../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-regular-svg-icons";

export interface IMddDataTableAssignmentRule
  extends Omit<
      IAssignmentRule,
      "ruleId" | "triggerCourse" | "triggerLearningPlan"
    >,
    IBktDataTableData {
  trigger: [string, string];
}

const getTypeOptions = (rules: IAssignmentRule[]): ISelectData[] =>
  uniq(rules.map(rule => rule.type)).map(type => ({
    value: type,
    label: type,
    key: type
  }));

const toDataTableRow = (
  rule: IAssignmentRule
): IMddDataTableAssignmentRule => ({
  key: rule.ruleId,
  ruleName: rule.ruleName,
  type: rule.type,
  trigger: [rule.triggerCourse, rule.triggerLearningPlan],
  assignLearningPlan: rule.assignLearningPlan,
  isActive: rule.isActive
});

const _MddShowAssignmentRules = (props: IMddStudyProps) => {
  const {
    study,
    match: {
      params: { id: routeStudyId }
    }
  } = props;

  const tableConfig = {
    ...defaultDataTableConfig,
    tableHeadProps: {
      ...defaultDataTableConfig.tableHeadProps,
      "data-testid": "tableHeader"
    },
    tableBodyProps: {
      ...defaultDataTableConfig.tableBodyProps,
      "data-testid": "tableBody"
    }
  };

  const { data, loading } = useSaga<IAssignmentRule[]>({
    startType: eAssignmentRuleSaga.FETCH_ASSIGNMENT_RULES,
    successType: eAssignmentRuleSaga.FETCH_ASSIGNMENT_RULES_SUCCESS,
    payload: routeStudyId
  });

  const [assignmentRules, setAssignmentRules] = React.useState<
    IMddDataTableAssignmentRule[] | undefined
  >();
  const [typeOptions, setTypeOptions] = React.useState<ISelectData[]>([]);
  const initialAssignmentRules = React.useRef<IAssignmentRule[] | undefined>();
  const filterVals = React.useRef(new Map<string, IFilter>());

  React.useEffect(() => {
    if (data) {
      initialAssignmentRules.current = data.map(m => ({
        ...m,
        trigger: `${m.triggerCourse} ${m.triggerLearningPlan}`
      }));
      setAssignmentRules(data.map(toDataTableRow));
      setTypeOptions(getTypeOptions(data));
    }
  }, [data]);

  const handleFilterChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    exactMatch = false
  ) => {
    const key = e.target.name;
    const value = e.target.value ? trim(e.target.value) : "";

    if (initialAssignmentRules.current) {
      const filteredData = filterDataForDataTable(
        { key, value: { value, exactMatch } },
        filterVals.current,
        initialAssignmentRules.current
      );
      if (filteredData) {
        setAssignmentRules(filteredData.map(toDataTableRow));
      }
    }
  };

  return (
    <MddDataContainer
      id="AssignmentRules"
      breadcrumbs={[
        { display: "All Studies", route: "/study" },
        { display: study.StudyName }
      ]}
      addAction={{
        display: "Add Rule",
        action: () => history.push(`/study/${routeStudyId}/assignmentrules/new`)
      }}
      title="Assignment Rules"
    >
      <BktDataTable
        data={assignmentRules}
        defaultSortProp="ruleName"
        loading={loading}
        columnProps={assignmentRulesDataTableHeaders(
          handleFilterChange,
          typeOptions,
          filterVals.current as Map<string, IFilter>
        )}
        rowActions={[
          {
            isPrimary: false,
            display: (
              <FontAwesomeIcon
                icon={faPenToSquare}
                fixedWidth
                id="editIcon"
                data-testid="svgEditIcon"
                className="svgEditIconPointer"
                title="Edit"
              />
            ),
            action: (data: IMddDataTableAssignmentRule) =>
              history.push(`/study/${routeStudyId}/assignmentrules/${data.key}`)
          }
        ]}
        noRecordsMessage={
          filterVals.current.size > 0 && !some(assignmentRules)
            ? messages.NO_RECORDS_FOUND
            : "No assignment rules exist for this study"
        }
        {...tableConfig}
      />
    </MddDataContainer>
  );
};

export const MddShowAssignmentRules = withMddStudy()(_MddShowAssignmentRules);
